import { useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import Modal from '../../shared/Modal';
import ModalFormActions from '../../shared/ModalFormActions';
import { TextInput, SelectInput } from '../../shared/FormControls';
import { useActiveCurrencyTypes } from '../../../hooks/admin/financeSettings/currencyTypes/useActiveCurrencyTypes';
import { useActivePartners } from '../../../hooks/shared/useActivePartners';
import { useProjects } from '../../../hooks/shared/useProjects';
import { useActiveContracts } from '../../../hooks/finance/useActiveContracts';

const initialFormState = {
  amount: '',
  currency: '',
  partner: '',
  project: '',
  contract: '',
  commitmentDate: '',
  budgetPercentage: '',
};

const FundCommitmentFormModal = ({
  isOpen,
  mode = 'create',
  fundCommitment,
  onSubmit,
  onClose,
  isSubmitting = false,
  serverError,
}) => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const { currencyTypes } = useActiveCurrencyTypes({ enabled: isOpen });
  const { partners } = useActivePartners({ enabled: isOpen });
  const { projects } = useProjects(
    { page: 1, pageSize: 100, isActive: true },
    { enabled: isOpen }
  );
  const { contracts } = useActiveContracts({ enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      setErrors({});

      if (mode === 'edit' && fundCommitment) {
        setFormValues({
          amount: fundCommitment.amount?.toString() ?? '',
          currency: fundCommitment.currency?._id || fundCommitment.currency || '',
          partner: fundCommitment.partner?._id || fundCommitment.partner || '',
          project: fundCommitment.project?._id || fundCommitment.project || '',
          contract: fundCommitment.contract?._id || fundCommitment.contract || '',
          commitmentDate: fundCommitment.commitmentDate
            ? new Date(fundCommitment.commitmentDate).toISOString().slice(0, 10)
            : '',
          budgetPercentage: fundCommitment.budgetPercentage?.toString() ?? '',
        });
      } else {
        setFormValues(initialFormState);
      }
    }
  }, [isOpen, mode, fundCommitment]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.amount || isNaN(parseFloat(formValues.amount))) {
      nextErrors.amount = 'Valid amount is required';
    }

    if (!formValues.currency) {
      nextErrors.currency = 'Currency is required';
    }

    if (!formValues.partner) {
      nextErrors.partner = 'Partner is required';
    }

    if (!formValues.project) {
      nextErrors.project = 'Project is required';
    }

    if (!formValues.contract) {
      nextErrors.contract = 'Contract is required';
    }

    if (!formValues.commitmentDate) {
      nextErrors.commitmentDate = 'Commitment date is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = {
      amount: parseFloat(formValues.amount),
      currency: formValues.currency,
      partner: formValues.partner,
      project: formValues.project,
      contract: formValues.contract,
      commitmentDate: formValues.commitmentDate,
      budgetPercentage: formValues.budgetPercentage
        ? parseFloat(formValues.budgetPercentage)
        : undefined,
    };

    onSubmit({ mode, payload });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Fund Commitment' : 'Create Fund Commitment'}
      size="2xl"
      icon={
        <Wallet
          className="h-6 w-6 text-primary-dark"
          strokeWidth={1.5}
        />
      }
      footer={
        <ModalFormActions
          onCancel={onClose}
          formId="fund-commitment-form"
          isSubmitting={isSubmitting}
          submitText={mode === 'edit' ? 'Save changes' : 'Create Fund Commitment'}
        />
      }
    >
      <form
        id="fund-commitment-form"
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        {serverError ? (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <TextInput
            id="amount"
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            value={formValues.amount}
            onChange={handleChange('amount')}
            placeholder="0"
            disabled={isSubmitting}
            error={errors.amount}
            required
          />

          <SelectInput
            id="currency"
            label="Currency"
            value={formValues.currency}
            onChange={handleChange('currency')}
            disabled={isSubmitting}
            error={errors.currency}
            required
          >
            <option value="">Select currency</option>
            {currencyTypes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.code} - {c.name}
              </option>
            ))}
          </SelectInput>

          <TextInput
            id="budgetPercentage"
            label="Budget %"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formValues.budgetPercentage}
            onChange={handleChange('budgetPercentage')}
            placeholder="e.g. 25"
            disabled={isSubmitting}
            error={errors.budgetPercentage}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <SelectInput
            id="partner"
            label="Partner"
            value={formValues.partner}
            onChange={handleChange('partner')}
            disabled={isSubmitting}
            error={errors.partner}
            required
          >
            <option value="">Select partner</option>
            {partners.map((p) => (
              <option key={p._id} value={p._id}>
                {p.acronym ? `${p.acronym} - ` : ''}{p.name}
              </option>
            ))}
          </SelectInput>

          <SelectInput
            id="project"
            label="Project"
            value={formValues.project}
            onChange={handleChange('project')}
            disabled={isSubmitting}
            error={errors.project}
            required
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectCode ? `${p.projectCode} - ` : ''}{p.name}
              </option>
            ))}
          </SelectInput>

          <SelectInput
            id="contract"
            label="Contract"
            value={formValues.contract}
            onChange={handleChange('contract')}
            disabled={isSubmitting}
            error={errors.contract}
            required
          >
            <option value="">Select contract</option>
            {contracts.map((c) => (
              <option key={c._id} value={c._id}>
                {c.contractNo ? `${c.contractNo} - ` : ''}{c.contractName || ''}
              </option>
            ))}
          </SelectInput>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <TextInput
            id="commitmentDate"
            label="Commitment Date"
            type="date"
            value={formValues.commitmentDate}
            onChange={handleChange('commitmentDate')}
            disabled={isSubmitting}
            error={errors.commitmentDate}
            required
          />
        </div>
      </form>
    </Modal>
  );
};

export default FundCommitmentFormModal;
